/**
 * React wrapper for Bryntum ProjectModel
 */
import React, { RefObject } from 'react';
import { AbstractCrudManager, AssignmentModel, AssignmentModelConfig, AssignmentStore, AssignmentStoreConfig, Base, CalendarManagerStore, CalendarManagerStoreConfig, CalendarModel, CalendarModelConfig, CoreStateTrackingManager, CrudManagerStoreDescriptor, DependencyModel, DependencyModelConfig, DependencyStore, DependencyStoreConfig, EventModel, EventModelConfig, EventStore, EventStoreConfig, Model, ModelConfig, ProjectModel, RelationConfig, ResourceAllocationInfo, ResourceModel, ResourceModelConfig, ResourceStore, ResourceStoreConfig, ResourceTimeRangeModel, ResourceTimeRangeModelConfig, ResourceTimeRangeStore, ResourceTimeRangeStoreConfig, SchedulerProjectModel, StateTrackingManagerConfig, Store, StoreConfig, TimeRangeStore, TimeSpan, TimeSpanConfig } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumProjectModelProps =  {
     // Configs only
    adjustDurationToDST?          : boolean
    assignmentModelClass?         : typeof AssignmentModel
    assignmentsData?              : AssignmentModel[]
    assignmentStoreClass?         : typeof AssignmentStore
    autoLoad?                     : boolean
    autoSync?                     : boolean
    autoSyncTimeout?              : number
    bubbleEvents?                 : object
    calendarManagerStoreClass?    : typeof CalendarManagerStore
    calendarModelClass?           : typeof CalendarModel
    calendarsData?                : CalendarModel[]
    callOnFunctions?              : boolean
    children?                     : boolean|object[]|Model[]|Partial<ModelConfig>[]
    delayCalculation?             : boolean
    dependenciesData?             : DependencyModel[]
    dependencyModelClass?         : typeof DependencyModel
    dependencyStoreClass?         : typeof DependencyStore
    encoder?                      : {
        requestData?: object
    }
    eventModelClass?              : typeof EventModel
    eventsData?                   : EventModel[]
    eventStoreClass?              : typeof EventStore
    expanded?                     : boolean
    listeners?                    : object
    maxCalendarRange?             : number
    orderedParentIndex?           : number
    parentId?                     : string|number|null
    parentIndex?                  : number
    phantomIdField?               : string
    phantomParentIdField?         : string
    resetIdsBeforeSync?           : boolean
    resetUndoRedoQueuesAfterLoad? : boolean
    resourceAllocationInfoClass?  : ResourceAllocationInfo
    resourceModelClass?           : typeof ResourceModel
    resourcesData?                : ResourceModel[]
    resourceStoreClass?           : typeof ResourceStore
    resourceTimeRangesData?       : ResourceTimeRangeModel[]
    resourceTimeRangeStoreClass?  : typeof ResourceTimeRangeStore
    silenceInitialCommit?         : boolean
    skipSuccessProperty?          : boolean
    storeIdProperty?              : string
    supportShortSyncResponse?     : boolean
    taskStore?                    : EventStore|Partial<EventStoreConfig>
    timeRangesData?               : TimeSpan[]
    timeRangeStoreClass?          : typeof TimeRangeStore
    trackResponseType?            : boolean
    transport?                    : {
        load?: object
        sync?: object
    }
    validateResponse?             : boolean
    writeAllFields?               : boolean

     // Configs and properties
    addConstraintOnDateSet?                             : boolean
    assignments?                                        : AssignmentModel[]|Partial<AssignmentModelConfig>[]
    assignmentStore?                                    : AssignmentStore|Partial<AssignmentStoreConfig>
    calendar?                                           : string|Partial<CalendarModelConfig>|CalendarModel
    calendarManagerStore?                               : CalendarManagerStore|Partial<CalendarManagerStoreConfig>
    calendars?                                          : CalendarModel[]|Partial<CalendarModelConfig>[]
    crudStores?                                         : CrudManagerStoreDescriptor[]|Store[]|string[]
    daysPerMonth?                                       : number
    daysPerWeek?                                        : number
    dependencies?                                       : DependencyModel[]|Partial<DependencyModelConfig>[]
    dependenciesCalendar?                               : 'ToEvent'|'FromEvent'|'Project'
    dependencyStore?                                    : DependencyStore|Partial<DependencyStoreConfig>
    enableProgressNotifications?                        : boolean
    events?                                             : EventModel[]|Partial<EventModelConfig>[]
    eventStore?                                         : EventStore|Partial<EventStoreConfig>
    forceSync?                                          : boolean
    hoursPerDay?                                        : number
    id?                                                 : string|number
    json?                                               : string
    loadUrl?                                            : string
    readOnly?                                           : boolean
    resources?                                          : ResourceModel[]|Partial<ResourceModelConfig>[]
    resourceStore?                                      : ResourceStore|Partial<ResourceStoreConfig>
    resourceTimeRanges?                                 : ResourceTimeRangeModel[]|Partial<ResourceTimeRangeModelConfig>[]
    resourceTimeRangeStore?                             : ResourceTimeRangeStore|Partial<ResourceTimeRangeStoreConfig>
    skipNonWorkingTimeInDurationWhenSchedulingManually? : boolean
    skipNonWorkingTimeWhenSchedulingManually?           : boolean
    stm?                                                : CoreStateTrackingManager|Partial<StateTrackingManagerConfig>
    syncApplySequence?                                  : CrudManagerStoreDescriptor[]|string[]
    syncUrl?                                            : string
    timeRanges?                                         : TimeSpan[]|Partial<TimeSpanConfig>[]
    timeRangeStore?                                     : Store|Partial<StoreConfig>
    timeZone?                                           : string|number
    trackProjectModelChanges?                           : boolean

     // Properties only
    allChildren?                : Model[]
    autoExposeFields?           : boolean
    childrenField?              : string
    convertEmptyParentToLeaf?   : boolean|{
        onLoad?: boolean
        onRemove?: boolean
    }
    defaults?                   : object
    descendantCount?            : number
    hasGeneratedId?             : boolean
    idField?                    : string
    inlineData?                 : object
    internalId?                 : number
    isCommitting?               : boolean
    isCreating?                 : boolean
    isValid?                    : boolean
    previousSiblingsTotalCount? : number
    relations?                  : Record<string, RelationConfig>
    visibleDescendantCount?     : number

     // Events
    onBeforeDestroy?       : ((event: { source: Base }) => void)|string
    onBeforeLoad?          : ((event: { source: AbstractCrudManager, pack: object }) => void)|string
    onBeforeLoadApply?     : ((event: { source: AbstractCrudManager, response: object, options: object }) => void)|string
    onBeforeResponseApply? : ((event: { source: AbstractCrudManager, requestType: 'sync'|'load', response: object }) => void)|string
    onBeforeSend?          : ((event: { crudManager: AbstractCrudManager, params: object, requestType: 'sync'|'load', requestConfig: object }) => Promise<void>)|string
    onBeforeSync?          : ((event: { source: AbstractCrudManager, pack: object }) => void)|string
    onBeforeSyncApply?     : ((event: { source: AbstractCrudManager, response: object }) => void)|string
    onCatchAll?            : ((event: {[key: string]: any, type: string}) => void)|string
    onChange?              : ((event: { source: SchedulerProjectModel|any, store: Store, action: 'remove'|'removeAll'|'add'|'updatemultiple'|'clearchanges'|'filter'|'update'|'dataset'|'replace', record: Model, records: Model[], changes: object }) => void)|string
    onCycle?               : ((event: { schedulingIssue: { getDescription: Function, cycle: object, getResolutions: Function }, continueWithResolutionResult: Function }) => void)|string
    onDataReady?           : ((event: { source: ProjectModel, isInitialCommit: boolean, records: Set<any> }) => void)|string
    onDestroy?             : ((event: { source: Base }) => void)|string
    onEmptyCalendar?       : ((event: { schedulingIssue: { getDescription: Function, getCalendar: Function, getResolutions: Function }, continueWithResolutionResult: Function }) => void)|string
    onHasChanges?          : ((event: { source: AbstractCrudManager }) => void)|string
    onLoad?                : ((event: { source: AbstractCrudManager, response: object, responseOptions: object }) => void)|string
    onLoadCanceled?        : ((event: { source: AbstractCrudManager, pack: object }) => void)|string
    onLoadFail?            : ((event: { source: AbstractCrudManager, response: object, responseText: string, responseOptions: object }) => void)|string
    onNoChanges?           : ((event: { source: AbstractCrudManager }) => void)|string
    onProgress?            : ((event: { total: number, remaining: number, phase: 'storePopulation'|'propagating' }) => void)|string
    onRequestDone?         : ((event: { source: AbstractCrudManager, requestType: 'sync'|'load', response: object, responseOptions: object }) => void)|string
    onRequestFail?         : ((event: { source: AbstractCrudManager, requestType: 'sync'|'load', response: object, responseText: string, responseOptions: object }) => void)|string
    onSchedulingConflict?  : ((event: { schedulingIssue: { getDescription: Function, intervals: object[], getResolutions: Function }, continueWithResolutionResult: Function }) => void)|string
    onSync?                : ((event: { source: AbstractCrudManager, response: object, responseOptions: object }) => void)|string
    onSyncCanceled?        : ((event: { source: AbstractCrudManager, pack: object }) => void)|string
    onSyncDelayed?         : ((event: { source: AbstractCrudManager, arguments: object }) => void)|string
    onSyncFail?            : ((event: { source: AbstractCrudManager, response: object, responseText: string, responseOptions: object }) => void)|string

}

export class BryntumProjectModel extends React.Component<BryntumProjectModelProps> {

    static instanceClass = ProjectModel;

    static instanceName = 'ProjectModel';

    processWidgetContent = processWidgetContent;

    dataStores = {
        'assignmentStore': 'assignments',
        'calendarManagerStore': 'calendars',
        'dependencyStore': 'dependencies',
        'eventStore': 'events',
        'resourceStore': 'resources',
        'resourceTimeRangeStore': 'resourceTimeRanges',
        'timeRangeStore': 'timeRanges'
    };

    static configNames = [
        'adjustDurationToDST',
        'assignmentModelClass',
        'assignmentsData',
        'assignmentStoreClass',
        'autoLoad',
        'autoSync',
        'autoSyncTimeout',
        'bubbleEvents',
        'calendarManagerStoreClass',
        'calendarModelClass',
        'calendarsData',
        'callOnFunctions',
        'children',
        'delayCalculation',
        'dependenciesData',
        'dependencyModelClass',
        'dependencyStoreClass',
        'encoder',
        'eventModelClass',
        'eventsData',
        'eventStoreClass',
        'expanded',
        'listeners',
        'maxCalendarRange',
        'orderedParentIndex',
        'parentId',
        'parentIndex',
        'phantomIdField',
        'phantomParentIdField',
        'resetIdsBeforeSync',
        'resetUndoRedoQueuesAfterLoad',
        'resourceAllocationInfoClass',
        'resourceModelClass',
        'resourcesData',
        'resourceStoreClass',
        'resourceTimeRangesData',
        'resourceTimeRangeStoreClass',
        'silenceInitialCommit',
        'skipSuccessProperty',
        'storeIdProperty',
        'supportShortSyncResponse',
        'taskStore',
        'timeRangesData',
        'timeRangeStoreClass',
        'trackResponseType',
        'transport',
        'validateResponse',
        'writeAllFields'
    ];

    static propertyConfigNames = [
        'addConstraintOnDateSet',
        'assignments',
        'assignmentStore',
        'calendar',
        'calendarManagerStore',
        'calendars',
        'crudStores',
        'daysPerMonth',
        'daysPerWeek',
        'dependencies',
        'dependenciesCalendar',
        'dependencyStore',
        'enableProgressNotifications',
        'events',
        'eventStore',
        'forceSync',
        'hoursPerDay',
        'id',
        'json',
        'loadUrl',
        'onBeforeDestroy',
        'onBeforeLoad',
        'onBeforeLoadApply',
        'onBeforeResponseApply',
        'onBeforeSend',
        'onBeforeSync',
        'onBeforeSyncApply',
        'onCatchAll',
        'onChange',
        'onCycle',
        'onDataReady',
        'onDestroy',
        'onEmptyCalendar',
        'onHasChanges',
        'onLoad',
        'onLoadCanceled',
        'onLoadFail',
        'onNoChanges',
        'onProgress',
        'onRequestDone',
        'onRequestFail',
        'onSchedulingConflict',
        'onSync',
        'onSyncCanceled',
        'onSyncDelayed',
        'onSyncFail',
        'readOnly',
        'resources',
        'resourceStore',
        'resourceTimeRanges',
        'resourceTimeRangeStore',
        'skipNonWorkingTimeInDurationWhenSchedulingManually',
        'skipNonWorkingTimeWhenSchedulingManually',
        'stm',
        'syncApplySequence',
        'syncUrl',
        'timeRanges',
        'timeRangeStore',
        'timeZone',
        'trackProjectModelChanges'
    ];

    static propertyNames = [
        'allChildren',
        'autoExposeFields',
        'childrenField',
        'convertEmptyParentToLeaf',
        'defaults',
        'descendantCount',
        'hasGeneratedId',
        'idField',
        'inlineData',
        'internalId',
        'isCommitting',
        'isCreating',
        'isValid',
        'previousSiblingsTotalCount',
        'relations',
        'visibleDescendantCount'
    ];

    // Component instance
    instance!: ProjectModel;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumProjectModelProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        return null

    }
}
